import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "../App";
import { api, extractApiErrorMessage } from "../services/apiClient";
import BackNavigationButton from "../components/navigation/BackNavigationButton";
import { useFormDraft } from "../hooks/useFormDraft";
import { useUnsavedChangesGuard } from "../hooks/useUnsavedChangesGuard";

const createInitialFormData = () => ({
  name: "",
  description: "",
  specialties: "",
  years_active: "",
  phone: "",
  website: "",
  image_url: "",
});

const DeveloperProfileEdit = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [formData, setFormData] = useState(createInitialFormData());
  const [initialSnapshot, setInitialSnapshot] = useState("");
  const draftKey = useMemo(
    () => `draft:developer-profile-edit:${user?.id || user?.email || "anon"}`,
    [user],
  );
  const isDirty = useMemo(() => {
    if (loadingData || !initialSnapshot) return false;
    return JSON.stringify(formData) !== initialSnapshot;
  }, [formData, initialSnapshot, loadingData]);
  const { clearDraft } = useFormDraft({
    draftKey,
    formData,
    setFormData,
    enabled: Boolean(user) && !loadingData,
  });
  const { confirmIfDirty } = useUnsavedChangesGuard(isDirty);

  const normalizeWebsite = (value) => {
    const trimmed = (value || "").trim();
    if (!trimmed) return null;

    if (/^https?:\/\//i.test(trimmed)) {
      return trimmed;
    }

    // Accept flexible formats like "dominio.com" or "www.dominio.com".
    return `https://${trimmed}`;
  };

  useEffect(() => {
    // Intentar cargar perfil de desarrollador
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await api.get("/developers/me/profile");

      if (response.ok) {
        const data = await response.json();
        const mappedFormData = {
          name: data.name || "",
          description: data.description || "",
          specialties: data.specialties?.join(", ") || "",
          years_active: data.years_active || "",
          phone: data.phone || "",
          website: data.website || "",
          image_url: data.image_url || "",
        };
        setFormData(mappedFormData);
        setInitialSnapshot(JSON.stringify(mappedFormData));
      } else if (response.status === 404) {
        // Perfil aún no creado: mantener formulario vacío para completarlo.
        setInitialSnapshot(JSON.stringify(createInitialFormData()));
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
      // Do not redirect — leave the empty form visible so the user can still complete it.
    } finally {
      setLoadingData(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const profileData = {
        name: formData.name,
        description: formData.description,
        specialties: formData.specialties
          ? formData.specialties.split(",").map((s) => s.trim())
          : [],
        years_active: formData.years_active
          ? parseInt(formData.years_active)
          : null,
        phone: formData.phone || null,
        website: normalizeWebsite(formData.website),
        image_url: formData.image_url || null,
      };

      const response = await api.put("/developers/me/profile", profileData);
      const responseText = await response.text();
      let data = null;
      try {
        data = responseText ? JSON.parse(responseText) : null;
      } catch {
        data = null;
      }

      if (!response.ok) {
        throw new Error(
          extractApiErrorMessage(data, "Error al actualizar perfil"),
        );
      }

      toast.success("¡Perfil actualizado exitosamente!");
      clearDraft();
      navigate("/dashboard");
    } catch (error) {
      if (
        String(error?.message || "")
          .toLowerCase()
          .includes("failed to fetch")
      ) {
        toast.error(
          "No se pudo conectar con el servidor. Verifica tu conexión e intenta de nuevo.",
        );
      } else {
        toast.error(error?.message || "Error al actualizar perfil");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (!confirmIfDirty()) return;
    navigate("/dashboard");
  };

  if (loadingData) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted">
      <Header />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 md:py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="mb-6 md:mb-8">
            <div className="mb-4">
              <BackNavigationButton
                onBeforeNavigate={confirmIfDirty}
                dataTestId="developer-profile-edit-back"
              />
            </div>
            <h1
              className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 tracking-tight"
              style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
              data-testid="profile-edit-title"
            >
              Editar Perfil de Desarrollador
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Actualiza la información pública de tu empresa
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="space-y-6 md:space-y-8 bg-background rounded-lg p-6 md:p-8"
          >
            <div className="space-y-6">
              <h2
                className="text-xl md:text-2xl font-bold"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Información Básica
              </h2>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Nombre de la Empresa *
                </label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                  data-testid="name-input"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Descripción *
                </label>
                <Textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Describe tu empresa, experiencia y servicios..."
                  rows={5}
                  required
                  data-testid="description-input"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  URL de Logo/Imagen
                </label>
                <Input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) =>
                    setFormData({ ...formData, image_url: e.target.value })
                  }
                  placeholder="https://ejemplo.com/logo.png"
                  data-testid="image-url-input"
                />
              </div>
            </div>

            <div className="space-y-6">
              <h2
                className="text-xl md:text-2xl font-bold"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Información Profesional
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Años de Experiencia
                  </label>
                  <Input
                    type="number"
                    value={formData.years_active}
                    onChange={(e) =>
                      setFormData({ ...formData, years_active: e.target.value })
                    }
                    placeholder="0"
                    data-testid="years-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Teléfono
                  </label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                    placeholder="+52 81 1234 5678"
                    data-testid="phone-input"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Sitio Web
                </label>
                <Input
                  type="text"
                  value={formData.website}
                  onChange={(e) =>
                    setFormData({ ...formData, website: e.target.value })
                  }
                  placeholder="ejemplo.com o https://ejemplo.com"
                  data-testid="website-input"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Especialidades (separadas por comas)
                </label>
                <Textarea
                  value={formData.specialties}
                  onChange={(e) =>
                    setFormData({ ...formData, specialties: e.target.value })
                  }
                  placeholder="Ej: Desarrollos Residenciales, Comerciales, Proyectos de Lujo"
                  rows={3}
                  data-testid="specialties-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Estas especialidades se mostrarán en tu perfil público
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-end gap-3 md:gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                className="w-full sm:w-auto"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="w-full sm:w-auto"
                data-testid="submit-button"
              >
                {loading ? "Guardando..." : "Guardar cambios"}
              </Button>
            </div>
          </form>
        </motion.div>
      </div>

      <Footer />
    </div>
  );
};

export default DeveloperProfileEdit;
